// src/App.tsx
import React, { useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Outlet,
} from 'react-router-dom';

import { useAuth } from './hooks/useAuth';
import { createCrudRoutes } from './router/routeWrappers';
import { isBeta, showDevTools } from './config/env';

import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import LoginScreen from './Layouts/Authentication/LoginScreen';
import LogoutModal from './Layouts/Authentication/LogoutModal';

// ── Page import ──────────────────────────────────────────────────────────────
import Dashboard                    from './Layouts/Dashboard/Dashboard';
import StudentsListDashboard        from './Layouts/Students/StudentsListDashboard';
import TeachersList                 from './Layouts/Teachers/TeachersList';
import Noticeboard                  from './Layouts/Noticeboard/Noticeboard';
import StudentIssues                from './Layouts/StudentIssues/StudentIssues';
import StudentCredentialsGenerator  from './Layouts/StudentCredentialsGenerator/StudentCredentialsGenerator';
import DateSheet                    from './Layouts/Exams/DateSheet';
import Timetable                    from './Layouts/Timetable/Timetable';
import BulkPasswordReset            from './Layouts/BulkPasswordReset/BulkPasswordReset';
import Settings                     from './Layouts/Settings/Settings';
import StudentPDFDownloader         from './Layouts/StudentsPdfGenerator/StudentPDFDownloader';
import HallTicketGenerator          from './Layouts/HallTicketGenerator/HallTicketGenerator';
import FeesPanel                    from './Layouts/FeesPanel/FeesPanel';
import Events                       from './Layouts/Events/Events';
import SchoolLetterHeader           from './Layouts/LetterHeaderGenerator/SchoolLetterHeader';
import SchoolLetterTemplate         from './Layouts/LetterHeaderGenerator/SchoolLetterTemplate';
import LetterheadListing            from './Layouts/LetterHeaderGenerator/LetterheadListing';
import LetterheadTemplateListing    from './Layouts/LetterHeaderGenerator/LetterheadTemplateListing';
import AssignedTeachersDashboard    from './Layouts/Timetable/sub-components/AssignedTeachersDashboard';
import BulkProfilePictureUpload     from './Layouts/Students/BulkProfilePictureUpload';
import StaffList                    from './Layouts/Staff/StaffList';
import UDISEAnomalyDetector         from './Layouts/StudentsPdfGenerator/UDISEAnomalyDetector';
import StaffAttendance              from './Layouts/StaffAttendance/StaffAttendance';
import GatePassGenerator            from './Layouts/HallTicketGenerator/GatePassGenerator';
import PayrollManagement            from './Layouts/PayrollManagement/PayrollManagement';
import TeacherCredentialsGenerator  from './Layouts/StudentCredentialsGenerator/TeacherCredentialsGenerator';
import AwardRole from './Layouts/AwardRole/AwardRole';
import PayrollRecords from './Layouts/PayrollManagement/PayrollRecords';
import ModernTimetablePage from './Layouts/Timetable/ModernTimetablePage';
import SubjectsDashboard from './Layouts/SubjectsSetup/SubjectsDashboard';
import ClassAttendanceOverview from './Layouts/StudentAttendance/ClassAttendanceOverview';
import FeeHistoryPanel from './Layouts/FeesPanel/FeeHistoryPanel';
import VisitorLogDashboard from './Layouts/VisitorLogs/VisitorLogDashboard';
import ExpenseDashboard from './Layouts/Expenses/ExpenseDashboard';
import { ACCESS, ruleOf } from './stateManagers/roleAccess/access';
import { Guard } from './stateManagers/roleAccess/useRoleAccess';
import GatePassDashboard from './Layouts/GatePasses/GatePassDashboard';
import GateEntryDashboard from './Layouts/GatePasses/GateEntryDashboard';

// ── CRUD route pairs ──────────────────────────────────────────────────────────
const Letterhead     = createCrudRoutes(SchoolLetterHeader,   '/letterheads');
const LetterTemplate = createCrudRoutes(SchoolLetterTemplate, '/letterhead-templates');

// ── Inner app (needs to be inside BrowserRouter for hooks) ────────────────────
const AppInner: React.FC = () => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  if (loading)          return <LoadingScreen />;
  if (!isAuthenticated) return <LoginScreen />;

  return (
    <>
      <Routes>
        {/*
         * Pathless layout route — MainLayout wraps every child via <Outlet />.
         */}
        <Route
          element={
            <MainLayout
              user={user}
              onNavigate={navigate}
              onLogout={() => setShowLogoutModal(true)}
            />
          }
        >
          {/* ── Dashboard ── */}
          <Route index element={<Dashboard />} />

          {/* ── Students — bulk photos has its own permission (no parent Guard) ── */}
          <Route path="students">
            <Route index
              element={<Guard rule={ruleOf('/students')}><StudentsListDashboard /></Guard>} />
            <Route path="bulk-photos"
              element={<Guard rule={ruleOf('/students/bulk-photos')}><BulkProfilePictureUpload /></Guard>} />
            <Route path="attendance"
              element={<Guard rule={ruleOf('/students/attendance')}><ClassAttendanceOverview /></Guard>} />
          </Route>

          {/* ── Teachers ── */}
          <Route path="teachers" element={<Guard rule={ACCESS.teachers}><TeachersList /></Guard>} />

          {/* ── Staff — each child has its own permission (no parent Guard) ── */}
          <Route path="staff">
            <Route index
              element={<Guard rule={ruleOf('/staff')}><StaffList /></Guard>} />
            <Route path="staff-attendance"
              element={<Guard rule={ruleOf('/staff/staff-attendance')}><StaffAttendance /></Guard>} />
          </Route>

          {/* ── Payroll — beta only ── */}
          {/* {isBeta && ( */}
            <Route path="payroll-management" element={<Guard rule={ACCESS['payroll-management']}><Outlet /></Guard>}>
              <Route index          element={<PayrollManagement />} />
              <Route path="records" element={<PayrollRecords />} />
            </Route>
          {/* )} */}

          {/* ── Noticeboard / Events ── */}
          <Route path="noticeboard" element={<Guard rule={ACCESS.noticeboard}><Noticeboard /></Guard>} />
          <Route path="events"      element={<Guard rule={ACCESS.events}><Events /></Guard>} />

          {/* ── Student Issues ── */}
          <Route path="student-issues" element={<Guard rule={ACCESS['student-issues']}><StudentIssues /></Guard>} />

          {/* ── Credentials ── */}
          <Route path="credentials" element={<Guard rule={ACCESS.credentials}><Outlet /></Guard>}>
            <Route path="students" element={<StudentCredentialsGenerator />} />
            <Route path="teachers" element={<TeacherCredentialsGenerator />} />
          </Route>

          {/* ── Misc flat screens ── */}
          <Route path="bulk-password-reset" element={<Guard rule={ACCESS['bulk-password-reset']}><BulkPasswordReset /></Guard>} />
          <Route path="exams" element={<Guard rule={ACCESS.exams}><Outlet /></Guard>}>
            <Route index               element={<DateSheet />} />
            <Route path="award-role"   element={<AwardRole />} />
          </Route>

          {/* ── Visitor Log ── */}
          <Route path="visitors" element={<Guard rule={ACCESS.visitors}><VisitorLogDashboard /></Guard>} />

          <Route path="gate-passes" element={<Guard rule={ACCESS['gate-passes']}><GatePassDashboard /></Guard>} />

          {/* ── Gate Entry ── */}
          <Route path="gate-entry" element={<Guard rule={ACCESS['gate-entry']}><GateEntryDashboard /></Guard>} />

          <Route path="expenses" element={<Guard rule={ACCESS.expenses}><ExpenseDashboard /></Guard>} />

          {/* ── Record Reports ── */}
          <Route path="students-pdf" element={<Guard rule={ACCESS['students-pdf']}><Outlet /></Guard>}>
            <Route index                       element={<StudentPDFDownloader />} />
            <Route path="UDISEAnomalyDetector" element={<UDISEAnomalyDetector />} />
          </Route>

          {/* ── Hall Tickets ── */}
          <Route path="hall-tickets" element={<Guard rule={ACCESS['hall-tickets']}><Outlet /></Guard>}>
            <Route index            element={<HallTicketGenerator />} />
            <Route path="gate-pass" element={<GatePassGenerator />} />
          </Route>

          {/* ── Fees / Settings ── */}
          <Route path="fees" element={<Guard rule={ACCESS.fees}><Outlet /></Guard>}>
            <Route index         element={<FeesPanel />} />
            <Route path="history" element={<FeeHistoryPanel />} />
          </Route>

          <Route path="settings" element={<Guard rule={ACCESS.settings}><Settings /></Guard>} />

          {/* ── Subjects — beta + local dev only ── */}
          {showDevTools && (
            <Route path="subjects" element={<Guard rule={ACCESS.subjects}><SubjectsDashboard /></Guard>} />
          )}

          {/* ── Timetable — assigned teachers has its own permission (no parent Guard) ── */}
          <Route path="timetable">
            <Route index
              element={<Guard rule={ruleOf('/timetable')}><Timetable /></Guard>} />
            <Route path="modern"
              element={<Guard rule={ruleOf('/timetable/modern')}><ModernTimetablePage /></Guard>} />
            <Route path="assigned-teachers"
              element={<Guard rule={ruleOf('/timetable/assigned-teachers')}><AssignedTeachersDashboard /></Guard>} />
          </Route>

          {/* ── Letterheads (list + CRUD) ── */}
          <Route path="letterheads" element={<Guard rule={ACCESS.letterheads}><Outlet /></Guard>}>
            <Route index           element={<LetterheadListing />} />
            <Route path="new"      element={<Letterhead.New />} />
            <Route path="edit/:id" element={<Letterhead.Edit />} />
          </Route>

          {/* ── Letter Templates (list + CRUD) ── */}
          <Route path="letterhead-templates" element={<Guard rule={ACCESS['letterhead-templates']}><Outlet /></Guard>}>
            <Route index           element={<LetterheadTemplateListing />} />
            <Route path="new"      element={<LetterTemplate.New />} />
            <Route path="edit/:id" element={<LetterTemplate.Edit />} />
          </Route>

          {/*
           * Template for future CRUD screens:
           *
           *   const Widget = createCrudRoutes(WidgetForm, '/widgets');
           *
           *   <Route path="widgets" element={<Guard rule={ACCESS.widgets}><Outlet /></Guard>}>
           *     <Route index           element={<WidgetList />} />
           *     <Route path="new"      element={<Widget.New />} />
           *     <Route path="edit/:id" element={<Widget.Edit />} />
           *   </Route>
           *
           * To give a child its own permission: drop the parent Guard and wrap each
           * child element in <Guard rule={ruleOf('/widgets/child')}>, after adding
           * the path to SUB_ACCESS in access.ts.
           *
           * For beta-only screens wrap in: {isBeta && ( <Route ...> )}
           */}

          {/* ── Catch-all ── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      <LogoutModal
        visible={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        onConfirm={async () => { await logout(); setShowLogoutModal(false); }}
      />
    </>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────
const App: React.FC = () => (
  <BrowserRouter>
    <AppInner />
  </BrowserRouter>
);

export default App;